
import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Scissors, Menu, X, LogOut, LogIn, User, Calendar } from 'lucide-react';
import { API_BASE, getImageUrl } from '../config';

export default function Navbar({ 
  lang, 
  setLang, 
  mobileMenuOpen, 
  setMobileMenuOpen, 
  t,
  onLogout,
  userRole,
  isAuthenticated,
  user
}) {
  const navigate = useNavigate();
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/settings`)
      .then(res => res.json())
      .then(data => {
         if (data.logo_image) setLogoUrl(data.logo_image);
      })
      .catch(err => console.error("Error loading logo", err));
  }, []);

  const navItems = [
    { path: '/', label: t.nav.home },
    { path: '/tarifs', label: t.nav.rates || 'Tarifs' },
    { path: '/reviews', label: t.nav.reviews || 'Avis' },
    { path: '/gallery', label: t.nav.gallery },
    { path: '/about', label: t.nav.about },
    { path: '/contact', label: t.nav.contact },
  ];

  if (userRole === 'admin') {
    navItems.push({ path: '/admin', label: t.nav.admin });
  }

  const flagUrls = {
    fr: 'https://flagcdn.com/fr.svg',
    en: 'https://flagcdn.com/gb.svg',
    pt: 'https://flagcdn.com/pt.svg'
  };

  const handleLogout = () => {
    onLogout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
             {logoUrl ? (
                <img 
                    src={getImageUrl(logoUrl)} 
                    alt="Hair Care & Relaxation Logo" 
                    className="h-12 w-auto object-contain" 
                />
             ) : (
                <>
                    <Scissors className="h-6 w-6 text-brand-600" />
                    <span className="ml-3 text-lg font-bold text-gray-800">Hair Care & Relaxation</span>
                </>
             )}
          </div>
          
          <div className="hidden md:flex space-x-8 items-center">
            <div className="flex space-x-8">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => 
                    `${isActive
                      ? 'text-brand-600 border-b-2 border-brand-600'
                      : 'text-gray-600 hover:text-brand-600'
                    } px-3 py-2 text-sm font-medium transition-colors`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>

            <div className="flex items-center space-x-4 border-l pl-6 border-gray-200">
              <div className="flex space-x-2">
                {['fr', 'en', 'pt'].map((language) => (
                  <button
                    key={language}
                    onClick={() => setLang(language)}
                    className={`${
                      lang === language
                        ? 'bg-gray-100 ring-2 ring-brand-500 transform scale-110'
                        : 'hover:bg-gray-100 opacity-70 hover:opacity-100'
                    } w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 shadow-sm border border-gray-200 overflow-hidden`}
                    title={language.toUpperCase()}
                  >
                    <img 
                      src={flagUrls[language]} 
                      alt={language} 
                      className="w-full h-full object-cover" 
                    />
                  </button>
                ))}
              </div>

              {isAuthenticated ? (
                  <div className="flex items-center space-x-3">
                      <div className="hidden lg:flex flex-col items-end text-right leading-tight">
                          <span className="text-[10px] font-black uppercase tracking-tighter text-slate-800">
                            {user?.firstname ? `Bonjour, ${user.firstname}` : t.nav.myAccount}
                          </span>
                          <span className="text-[9px] font-bold text-brand-500 uppercase tracking-widest">
                            {userRole === 'admin' ? t.nav.roleAdmin : t.nav.roleUser}
                          </span>
                      </div>
                      
                      <button
                          onClick={() => navigate('/my-bookings')}
                          className="p-2 text-gray-400 hover:text-brand-600 transition-colors"
                          title={t.nav.myBookings || 'Mes RDV'}
                      >
                          <Calendar className="h-6 w-6" />
                      </button>

                      <button
                          onClick={() => navigate('/profile')}
                          className="p-2 text-gray-400 hover:text-brand-600 transition-colors"
                          title={t.nav.profile}
                      >
                          <User className="h-6 w-6" />
                      </button>
                      <button
                          onClick={handleLogout}
                          className="flex items-center text-gray-500 hover:text-red-600 transition-colors"
                          title={t.nav.logout}
                      >
                          <LogOut className="h-6 w-6" />
                      </button>
                  </div>
              ) : (
                  <button
                      onClick={() => navigate('/login')}
                      className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-full hover:bg-brand-700 transition-colors shadow-md"
                  >
                      <LogIn className="h-4 w-4 mr-2" />
                      <span className="font-medium">{t.nav.login}</span>
                  </button>
              )}
            </div>
          </div>

          <div className="md:hidden flex items-center space-x-4">
             <div className="flex space-x-2">
                {['fr', 'en', 'pt'].map((language) => (
                  <button
                    key={language}
                    onClick={() => setLang(language)}
                    className={`${
                      lang === language
                        ? 'bg-gray-100 ring-1 ring-brand-500'
                        : 'opacity-70'
                    } w-6 h-6 flex items-center justify-center rounded-full border border-gray-200 overflow-hidden`}
                  >
                    <img 
                      src={flagUrls[language]} 
                      alt={language} 
                      className="w-full h-full object-cover" 
                    />
                  </button>
                ))}
             </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-600 hover:text-brand-600 focus:outline-none"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {isAuthenticated && (
              <div className="px-3 py-2 mb-2 bg-slate-50 rounded-lg">
                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest leading-none mb-1">Session active</p>
                <p className="text-sm font-bold text-slate-800 capitalize">{user?.firstname ? `Bonjour, ${user.firstname}` : t.nav.myAccount}</p>
              </div>
            )}
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `${isActive
                    ? 'text-brand-600 bg-brand-50'
                    : 'text-gray-600 hover:text-brand-600 hover:bg-gray-50'
                  } block px-3 py-2 rounded-md text-base font-medium`
                }
              >
                {item.label}
              </NavLink>
            ))}
            
            <div className="border-t border-gray-100 pt-4 mt-4">
                {isAuthenticated ? (
                    <div className="space-y-1">
                         <button
                            onClick={() => {
                                navigate('/my-bookings');
                                setMobileMenuOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 text-base font-medium text-gray-600 hover:text-brand-600 hover:bg-gray-50 rounded-md flex items-center"
                         >
                            <Calendar className="h-5 w-5 mr-3" />
                            {t.nav.myBookings || 'Mes RDV'}
                         </button>
                         <button
                            onClick={() => {
                                navigate('/profile');
                                setMobileMenuOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 text-base font-medium text-gray-600 hover:text-brand-600 hover:bg-gray-50 rounded-md flex items-center"
                         >
                            <User className="h-5 w-5 mr-3" />
                            {t.nav.profile}
                         </button>
                         <button
                            onClick={handleLogout}
                            className="w-full text-left px-3 py-2 text-base font-medium text-red-600 hover:bg-red-50 rounded-md flex items-center"
                         >
                            <LogOut className="h-5 w-5 mr-3" />
                            {t.nav.logout}
                         </button>
                    </div>
                ) : (
                    <button
                        onClick={() => {
                            navigate('/login');
                            setMobileMenuOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-base font-medium text-gray-600 hover:text-brand-600 hover:bg-gray-50 rounded-md flex items-center"
                    >
                        <LogIn className="h-5 w-5 mr-3" />
                        {t.nav.login}
                    </button>
                )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
